import React from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router';
import {
    mdiAlertCircle, mdiAlert, mdiInformation, mdiCheckCircle, mdiBellOff, mdiPlusBoxOutline,
    mdiSend, mdiPencil, mdiOpenInNew,
} from '@mdi/js';
import Icon from '@mdi/react';

import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Grid, { GridItem } from '@/components/layout/Grid';
import AlertBox from '@/components/layout/AlertBox';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Tabs from '@/components/layout/Tabs';
import Modal from '@/components/layout/Modal';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import useWindowSize from '@/lib/useWindowSize';
import { useAuth } from '@/lib/useAuth';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { ALERTAS_QUERY_KEY, ALERTAS_RESUMO_QUERY_KEY } from '@/lib/useAlertas';
import FaixaDoTempo from './FaixaDoTempo';
import { useAppliance } from '@/lib/useAppliance';

// Só tipos: o vite não resolve import de runtime de @webip/shared.
import type { AlertaDTO, AlertaContatoDTO, AlertaCanaisDTO, AlertaSeveridade } from '@webip/shared/src/dto/alerta';
import type { ApplianceDTO } from '@webip/shared/src/dto/appliance';

const SEVERIDADE: Record<AlertaSeveridade, { label: string; icon: string; color: string; background: string }> = {
    critico: {
        label: 'Crítico', icon: mdiAlertCircle,
        color: 'var(--tc-text-color-danger-emphasis)', background: 'var(--tc-background-color-danger-subtle)',
    },
    aviso: {
        label: 'Aviso', icon: mdiAlert,
        color: 'var(--tc-text-color-warning-emphasis)', background: 'var(--tc-background-color-warning-subtle)',
    },
    info: {
        label: 'Informativo', icon: mdiInformation,
        color: 'var(--tc-text-color-info-emphasis)', background: 'var(--tc-background-color-info-subtle)',
    },
};

const COR_BORDA: Record<AlertaSeveridade, string> = {
    critico: 'var(--tc-color-danger)',
    aviso: 'var(--tc-color-warning)',
    info: 'var(--tc-color-info)',
};

const OPCOES_SEVERIDADE = (Object.keys(SEVERIDADE) as AlertaSeveridade[]).map(s => ({ id: s, label: SEVERIDADE[s].label }));

const Pill = ({ label, color, background, icon, title }: { label: string; color: string; background: string; icon?: string; title?: string }) => (
    <span
        title={title}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
            color, backgroundColor: background, whiteSpace: 'nowrap',
        }}
    >
        {icon && <Icon path={icon} size="13px" />}
        {label}
    </span>
);

const absoluto = (v?: string | null) => (v ? new Date(v).toLocaleString() : undefined);

/** "3 min", "2 h 10 min", "4 d 3 h" — a duração do painel de problemas. */
function duracao(inicio: string, fim?: string | null): string {
    const min = Math.max(0, Math.round(((fim ? new Date(fim).getTime() : Date.now()) - new Date(inicio).getTime()) / 60_000));
    if (min < 1) return 'menos de 1 min';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
    const d = Math.floor(h / 24);
    return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

// ─── Problemas ────────────────────────────────────────────────────────────────

const AbaProblemas = ({ appliances }: { appliances: ApplianceDTO[] }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    // As telas do link (Redes, Dispositivos, VPN…) mostram a "Appliance ativa"
    // da barra lateral: abrir o alerta troca para a appliance dele antes.
    const { setApplianceId: setApplianceAtiva } = useAppliance();
    const abrir = (row: AlertaDTO) => {
        if (row.appliance_id != null) setApplianceAtiva(String(row.appliance_id));
        navigate(row.link!);
    };
    const [mostrarNormalizados, setMostrarNormalizados] = React.useState(false);
    const [applianceId, setApplianceId] = React.useState<string | null>(null);
    const [expandido, setExpandido] = React.useState<number | null>(null);
    const [diasFaixa, setDiasFaixa] = React.useState(7);

    const query = useQuery({
        queryKey: [ALERTAS_QUERY_KEY, applianceId],
        queryFn: async () => {
            const res = await axios.get<{ result: AlertaDTO[] }>('/api/alerta', {
                params: { appliance_id: applianceId ?? undefined, dias: 30 },
                withCredentials: true,
            });
            return res.data.result;
        },
        refetchInterval: 30_000,
    });

    const mutSuprimir = useMutation({
        mutationFn: async (id: number) => axios.post(`/api/alerta/${id}/suprimir`, {}, { withCredentials: true }),
        onSuccess: () => {
            toastSuccess('Avisos suprimidos. Você ainda será avisado quando o problema normalizar.');
            queryClient.invalidateQueries(ALERTAS_QUERY_KEY);
            queryClient.invalidateQueries(ALERTAS_RESUMO_QUERY_KEY);
        },
        onError: (error: unknown) => toastError(extractApiError(error, 'Erro ao suprimir os avisos').message),
    });

    const todos = query.data ?? [];
    const ativos = todos.filter(a => a.estado === 'firing');
    const linhas = mostrarNormalizados ? todos : ativos;

    const opcoesAppliance = appliances.map(a => ({ id: String(a.id), label: a.nome ?? `#${a.id}` }));

    const headers: DataTableHeader[] = [
        {
            column: 'severidade',
            label: 'Severidade',
            mobileOrder: 0,
            render: (row: AlertaDTO) => {
                const s = SEVERIDADE[row.severidade];
                return row.estado === 'firing'
                    ? <Pill label={s.label} icon={s.icon} color={s.color} background={s.background} />
                    : <Pill label="Normalizado" icon={mdiCheckCircle} color="var(--tc-text-color-success-emphasis)" background="var(--tc-background-color-success-subtle)" />;
            },
        },
        {
            column: 'titulo',
            label: 'Problema',
            mobileOrder: 1,
            render: (row: AlertaDTO) => (
                <VBox style={{ gap: '4px', alignItems: 'flex-start', minWidth: '260px' }}>
                    <button
                        type="button"
                        onClick={() => setExpandido(v => (v === row.id ? null : row.id))}
                        style={{
                            all: 'unset', cursor: 'pointer', fontWeight: 600,
                            color: row.estado === 'firing' ? 'inherit' : 'var(--tc-color-gray-600)',
                        }}
                        title="Detalhes e como resolver"
                    >
                        {row.titulo}
                    </button>
                    {row.data_ack && row.estado === 'firing' && (
                        <Pill
                            label="Avisos suprimidos"
                            icon={mdiBellOff}
                            color="var(--tc-color-gray-700)"
                            background="var(--tc-color-gray-200)"
                            title={`Por ${row.ack_usuario_nome ?? 'link do e-mail'} em ${absoluto(row.data_ack)}`}
                        />
                    )}
                    {expandido === row.id && (
                        <VBox style={{ gap: '8px', fontSize: '13px', marginTop: '4px', maxWidth: '640px' }}>
                            {row.mensagem && <span style={{ lineHeight: 1.5 }}>{row.mensagem}</span>}
                            {row.orientacao && row.estado === 'firing' && (
                                <div style={{ padding: '8px 12px', borderRadius: '6px', background: 'var(--tc-color-gray-100)', lineHeight: 1.5 }}>
                                    <b>Como resolver: </b>{row.orientacao}
                                </div>
                            )}
                            <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                                Visto pela última vez em {absoluto(row.data_ultimo_evento)}
                                {row.data_resolucao ? ` · normalizado em ${absoluto(row.data_resolucao)}` : ''}
                            </span>
                        </VBox>
                    )}
                </VBox>
            ),
        },
        {
            column: 'appliance_nome',
            label: 'Appliance',
            mobileOrder: 2,
            render: (row: AlertaDTO) => <span>{row.appliance_nome ?? '—'}</span>,
        },
        {
            column: 'data_inicio',
            label: 'Início',
            mobileOrder: 3,
            render: (row: AlertaDTO) => <span title={absoluto(row.data_inicio)}>{new Date(row.data_inicio).toLocaleString()}</span>,
        },
        {
            column: 'duracao',
            label: 'Duração',
            mobileOrder: 4,
            render: (row: AlertaDTO) => <span>{duracao(row.data_inicio, row.data_resolucao)}</span>,
        },
        {
            column: 'acoes',
            label: 'Ações',
            mobileOrder: 5,
            alignRight: true,
            render: (row: AlertaDTO) => (
                <HBox style={{ gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {row.link && row.estado === 'firing' && (
                        <Botao size="sm" variant="outline-primary" icon={mdiOpenInNew} onClick={() => abrir(row)}>
                            Abrir
                        </Botao>
                    )}
                    {row.estado === 'firing' && !row.data_ack && (
                        <Botao
                            size="sm"
                            variant="outline-secondary"
                            icon={mdiBellOff}
                            disabled={mutSuprimir.isLoading}
                            title="Para os lembretes deste problema. A normalização e uma nova ocorrência continuam avisando."
                            onClick={() => mutSuprimir.mutate(row.id)}
                        >
                            Suprimir avisos
                        </Botao>
                    )}
                </HBox>
            ),
        },
    ];

    const contagem = (s: AlertaSeveridade) => ativos.filter(a => a.severidade === s).length;

    return (
        <VBox style={{ gap: '16px' }}>
            <HBox style={{ gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {(Object.keys(SEVERIDADE) as AlertaSeveridade[]).map(s => (
                    <Pill
                        key={s}
                        label={`${contagem(s)} ${SEVERIDADE[s].label.toLowerCase()}${contagem(s) === 1 ? '' : 's'}`}
                        icon={SEVERIDADE[s].icon}
                        color={contagem(s) ? SEVERIDADE[s].color : 'var(--tc-color-gray-600)'}
                        background={contagem(s) ? SEVERIDADE[s].background : 'var(--tc-color-gray-100)'}
                    />
                ))}
            </HBox>

            <HBox style={{ gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <div style={{ minWidth: '240px' }}>
                    <CampoSeletor
                        nullable
                        label="Appliance"
                        placeholder="(Todas)"
                        value={applianceId}
                        onChange={setApplianceId}
                        options={opcoesAppliance}
                    />
                </div>
                <HBox style={{ gap: '4px', alignItems: 'center' }} >
                    {[{ d: 1, l: '24 h' }, { d: 7, l: '7 dias' }, { d: 30, l: '30 dias' }].map(o => (
                        <Botao
                            key={o.d}
                            size="sm"
                            variant={diasFaixa === o.d ? 'primary' : 'outline-secondary'}
                            aria-pressed={diasFaixa === o.d}
                            onClick={() => setDiasFaixa(o.d)}
                        >
                            {o.l}
                        </Botao>
                    ))}
                </HBox>
            </HBox>

            <FaixaDoTempo dias={diasFaixa} applianceId={applianceId} />

            <HBox style={{ justifyContent: 'flex-end' }}>
                <Botao variant="none-secondary" size="sm" onClick={() => setMostrarNormalizados(v => !v)}>
                    {mostrarNormalizados ? 'Mostrar só os ativos' : 'Mostrar normalizados (30 dias)'}
                </Botao>
            </HBox>

            {query.isError ? (
                <AlertBox variant="danger">Erro ao carregar os alertas.</AlertBox>
            ) : linhas.length === 0 && !query.isFetching ? (
                <HBox style={{ gap: '8px', alignItems: 'center', color: 'var(--tc-color-gray-700)' }}>
                    <Icon path={mdiCheckCircle} size="20px" color="var(--tc-color-success)" />
                    {mostrarNormalizados ? 'Nenhum alerta nos últimos 30 dias.' : 'Nenhum problema ativo.'}
                </HBox>
            ) : (
                <DataTable
                    isLoading={query.isFetching && !query.data}
                    cabecalhos={headers}
                    linhas={linhas}
                    rowStyle={(row: AlertaDTO) => ({
                        borderLeft: `4px solid ${row.estado === 'firing' ? COR_BORDA[row.severidade] : 'transparent'}`,
                    })}
                />
            )}
        </VBox>
    );
};

// ─── Contatos ─────────────────────────────────────────────────────────────────

type ChatTelegram = { id: string; nome: string; tipo: string };

/**
 * Conversas que falaram com o bot do tenant nas últimas 24 h — a pessoa manda
 * /start ao bot (ou o bot entra no grupo) e aparece aqui; clicar preenche o chat_id.
 */
const SeletorChatTelegram = ({ habilitado, onEscolher }: { habilitado: boolean; onEscolher: (id: string) => void }) => {
    const [aberto, setAberto] = React.useState(false);
    const query = useQuery({
        queryKey: ['alerta-telegram-chats'],
        queryFn: async () => {
            const res = await axios.get<{ result: ChatTelegram[] }>('/api/alerta/telegram/chats', { withCredentials: true });
            return res.data.result;
        },
        enabled: aberto && habilitado,
        retry: false,
    });

    if (!habilitado) {
        return (
            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                Configure o bot em <Link to="/configuracoes">Configurações gerais</Link> para enviar pelo Telegram.
            </span>
        );
    }

    return (
        <VBox style={{ gap: '6px', alignItems: 'flex-start', marginTop: '4px' }}>
            <HBox style={{ gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                    A pessoa manda /start ao bot (ou adiciona o bot ao grupo) e depois:
                </span>
                <Botao
                    size="sm"
                    variant="none-primary"
                    disabled={query.isFetching}
                    onClick={() => (aberto ? query.refetch() : setAberto(true))}
                >
                    {query.isFetching ? 'Buscando...' : aberto ? 'Buscar de novo' : 'Buscar conversas'}
                </Botao>
            </HBox>
            {aberto && query.isError && (
                <span style={{ fontSize: '12px', color: 'var(--tc-text-color-danger-emphasis)' }}>
                    {extractApiError(query.error, 'Erro ao consultar o Telegram').message}
                </span>
            )}
            {aberto && query.data && (query.data.length === 0 ? (
                <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                    Nenhuma conversa nas últimas 24 h.
                </span>
            ) : (
                <HBox style={{ gap: '6px', flexWrap: 'wrap' }}>
                    {query.data.map(c => (
                        <Botao key={c.id} size="sm" variant="outline-secondary" onClick={() => onEscolher(c.id)}>
                            {c.nome}{c.tipo !== 'private' ? ' (grupo)' : ''}
                        </Botao>
                    ))}
                </HBox>
            ))}
        </VBox>
    );
};

const CONTATO_VAZIO: AlertaContatoDTO = {
    nome: '', email: null, telegram_chat_id: null, severidades: ['critico', 'aviso'], appliance_ids: null, ativo: true,
};

const AbaContatos = ({ appliances }: { appliances: ApplianceDTO[] }) => {
    const [editando, setEditando] = React.useState<AlertaContatoDTO | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});

    const query = useQuery({
        queryKey: ['alerta-contatos'],
        queryFn: async () => {
            const res = await axios.get<{ result: AlertaContatoDTO[] }>('/api/alerta/contatos', { withCredentials: true });
            return res.data.result;
        },
    });

    const queryCanais = useQuery({
        queryKey: ['alerta-canais'],
        queryFn: async () => {
            const res = await axios.get<{ result: AlertaCanaisDTO }>('/api/alerta/canais', { withCredentials: true });
            return res.data.result;
        },
    });

    const fechar = () => { setEditando(null); setFieldErrors({}); };

    const mutSalvar = useMutation({
        mutationFn: async (c: AlertaContatoDTO) => (c.id
            ? axios.put(`/api/alerta/contatos/${c.id}`, c, { withCredentials: true })
            : axios.post('/api/alerta/contatos', c, { withCredentials: true })),
        onSuccess: () => { fechar(); query.refetch(); toastSuccess('Contato salvo.'); },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao salvar o contato');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutExcluir = useMutation({
        mutationFn: async (id: number) => axios.delete(`/api/alerta/contatos/${id}`, { withCredentials: true }),
        onSuccess: () => { query.refetch(); toastSuccess('Contato excluído.'); },
        onError: (error: unknown) => toastError(extractApiError(error, 'Erro ao excluir o contato').message),
    });

    const mutTestar = useMutation({
        mutationFn: async (id: number) => {
            const res = await axios.post<{ result: { canal: string; ok: boolean; erro?: string }[] }>(
                `/api/alerta/contatos/${id}/testar`, {}, { withCredentials: true },
            );
            return res.data.result;
        },
        onSuccess: (result) => {
            if (!result.length) {
                toastError('Nenhum canal deste contato está configurado no servidor.');
                return;
            }
            const falhas = result.filter(r => !r.ok);
            if (falhas.length) toastError(`Falhou: ${falhas.map(f => `${f.canal} (${f.erro})`).join('; ')}`);
            else toastSuccess(`Teste enviado por ${result.map(r => r.canal === 'email' ? 'e-mail' : 'Telegram').join(' e ')}.`);
        },
        onError: (error: unknown) => toastError(extractApiError(error, 'Erro ao enviar o teste').message),
    });

    const nomeAppliance = (id: number) => appliances.find(a => a.id === id)?.nome ?? `#${id}`;
    const opcoesAppliance = appliances.map(a => ({ id: String(a.id), label: a.nome ?? `#${a.id}` }));
    const canais = queryCanais.data;

    const headers: DataTableHeader[] = [
        {
            column: 'nome',
            label: 'Contato',
            mobileOrder: 0,
            render: (row: AlertaContatoDTO) => (
                <VBox style={{ gap: '2px', alignItems: 'flex-start' }}>
                    <HBox style={{ gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>{row.nome}</span>
                        {!row.ativo && <Pill label="Inativo" color="var(--tc-color-gray-700)" background="var(--tc-color-gray-200)" />}
                    </HBox>
                    {row.email && <span style={{ fontSize: '12px' }}>{row.email}</span>}
                    {row.telegram_chat_id && <span style={{ fontSize: '12px' }}>Telegram {row.telegram_chat_id}</span>}
                </VBox>
            ),
        },
        {
            column: 'severidades',
            label: 'Recebe',
            mobileOrder: 1,
            render: (row: AlertaContatoDTO) => (
                <HBox style={{ gap: '4px', flexWrap: 'wrap' }}>
                    {row.severidades.map(s => (
                        <Pill key={s} label={SEVERIDADE[s].label} icon={SEVERIDADE[s].icon} color={SEVERIDADE[s].color} background={SEVERIDADE[s].background} />
                    ))}
                </HBox>
            ),
        },
        {
            column: 'appliance_ids',
            label: 'Appliances',
            mobileOrder: 2,
            render: (row: AlertaContatoDTO) => (
                <span style={{ fontSize: '12px' }}>
                    {row.appliance_ids?.length ? row.appliance_ids.map(nomeAppliance).join(', ') : 'Todas'}
                </span>
            ),
        },
        {
            column: 'acoes',
            label: 'Ações',
            mobileOrder: 3,
            alignRight: true,
            render: (row: AlertaContatoDTO) => (
                <HBox style={{ gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Botao size="sm" variant="outline-secondary" icon={mdiSend} disabled={mutTestar.isLoading} onClick={() => mutTestar.mutate(row.id!)}>
                        Testar
                    </Botao>
                    <Botao size="sm" variant="outline-primary" icon={mdiPencil} onClick={() => setEditando({ ...row })}>
                        Editar
                    </Botao>
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        confirmation={`Excluir o contato "${row.nome}"? Ele deixa de receber os alertas.`}
                        onConfirm={() => mutExcluir.mutate(row.id!)}
                    >
                        Excluir
                    </BotaoPerigoso>
                </HBox>
            ),
        },
    ];

    return (
        <VBox style={{ gap: '16px' }}>
            {canais && (!canais.email || !canais.telegram) && (
                <AlertBox variant="warning">
                    <VBox style={{ gap: '4px', alignItems: 'flex-start' }}>
                        {!canais.telegram && (
                            <span>
                                O bot do Telegram não está configurado — configure-o em{' '}
                                <Link to="/configuracoes">Configurações gerais</Link>.
                            </span>
                        )}
                        {!canais.email && <span>O envio de e-mail não está configurado no servidor (EMAIL_HOST).</span>}
                        {!canais.email && !canais.telegram && <span>Até lá, os alertas aparecem só nesta tela.</span>}
                    </VBox>
                </AlertBox>
            )}

            <HBox style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-700)', maxWidth: '720px' }}>
                    Cada contato recebe um aviso quando o problema começa, lembretes enquanto ele durar
                    (críticos a cada 4 h, avisos a cada 24 h, até alguém suprimir) e outro quando normalizar.
                </span>
                <Botao variant="primary" icon={mdiPlusBoxOutline} onClick={() => setEditando({ ...CONTATO_VAZIO })}>
                    Novo contato
                </Botao>
            </HBox>

            {query.isError ? (
                <AlertBox variant="danger">Erro ao carregar os contatos.</AlertBox>
            ) : (query.data?.length ?? 0) === 0 && !query.isFetching ? (
                <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                    Nenhum contato cadastrado — os alertas aparecem só nesta tela.
                </span>
            ) : (
                <DataTable isLoading={query.isFetching && !query.data} cabecalhos={headers} linhas={query.data ?? []} />
            )}

            <Modal
                instanceId="modal-alerta-contato"
                size="md"
                title={editando?.id ? 'Editar contato' : 'Novo contato'}
                show={!!editando}
                onClose={fechar}
                onCancel={fechar}
                onConfirm={() => editando && mutSalvar.mutate(editando)}
                confirmProps={{ label: 'Salvar', disabled: !editando?.nome.trim() || mutSalvar.isLoading }}
            >
                {editando && (
                    <Grid>
                        <GridItem sm={12}>
                            <CampoTexto
                                label="Nome"
                                required
                                placeholder="Ex: NOC, Fulano (TI)"
                                value={editando.nome}
                                onChange={(v: string) => setEditando({ ...editando, nome: v })}
                                error={fieldErrors.nome}
                            />
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            <CampoTexto
                                label="E-mail"
                                placeholder="nome@empresa.com"
                                value={editando.email ?? ''}
                                onChange={(v: string) => setEditando({ ...editando, email: v || null })}
                                error={fieldErrors.email}
                            />
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            <CampoTexto
                                label="Telegram (chat_id)"
                                placeholder="Ex: 123456789"
                                value={editando.telegram_chat_id ?? ''}
                                onChange={(v: string) => setEditando({ ...editando, telegram_chat_id: v || null })}
                                error={fieldErrors.telegram_chat_id}
                            />
                            <SeletorChatTelegram
                                habilitado={!!canais?.telegram}
                                onEscolher={(id) => setEditando({ ...editando, telegram_chat_id: id })}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoMultiSeletor
                                label="Severidades"
                                noSearch
                                value={editando.severidades}
                                onChange={(v: string[]) => setEditando({ ...editando, severidades: v as AlertaSeveridade[] })}
                                options={OPCOES_SEVERIDADE}
                                error={fieldErrors.severidades}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoMultiSeletor
                                label="Appliances"
                                placeholder="(Todas)"
                                value={(editando.appliance_ids ?? []).map(String)}
                                onChange={(v: string[]) => setEditando({ ...editando, appliance_ids: v.length ? v.map(Number) : null })}
                                options={opcoesAppliance}
                                error={fieldErrors.appliance_ids}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoCheckbox
                                text="Ativo"
                                value={editando.ativo}
                                onChange={(v) => setEditando({ ...editando, ativo: v })}
                            />
                        </GridItem>
                    </Grid>
                )}
            </Modal>
        </VBox>
    );
};

// ─── Página ───────────────────────────────────────────────────────────────────

const Page = () => {
    const [, , isMobile] = useWindowSize();
    const { user } = useAuth();
    const admin = user?.papel === 'ADMIN';
    const [aba, setAba] = React.useState('problemas');

    const queryAppliances = useQuery({
        queryKey: ['alertas-appliances'],
        queryFn: async () => {
            const res = await axios.get<{ result: ApplianceDTO[] }>('/api/appliance', { withCredentials: true });
            return res.data.result ?? [];
        },
    });
    const appliances = queryAppliances.data ?? [];

    const tabs = [
        { id: 'problemas', label: 'Problemas', content: <AbaProblemas appliances={appliances} /> },
        ...(admin ? [{ id: 'contatos', label: 'Contatos', content: <AbaContatos appliances={appliances} /> }] : []),
    ];

    return (
        <LayoutUsuario>
            <VBox style={{ padding: isMobile ? '16px' : '24px', gap: '16px', height: '100%', overflow: 'auto' }}>
                <PageHeader
                    title="Alertas"
                    description="Problemas detectados nas appliances e nos agentes. Vermelho: conectividade interrompida ou parcialmente interrompida; amarelo: prejudicada, mas funcional; azul: informativo."
                />
                <Tabs tabs={tabs} selectedTab={aba} onTabChange={setAba} position="start" size="lg" />
            </VBox>
        </LayoutUsuario>
    );
};

export default Page;
